import { Box, Flex, Text } from '@chakra-ui/react';
import { faCopy, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, ButtonIcon } from '@morressier/alembic';
import { IRecommendationParticipant } from '@publishing-engine/domain/models';
import RecommendationBadge from 'src/components/RecommendationBadge';
import { submissionStatusBadges } from 'src/components/Review/Badges';
import { useCOCContextData } from 'src/contexts/COCDataProvider';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { formatDate } from 'src/utils/date';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import FileViewerButtonText from 'src/components/Submission/FileViewerButtonText';
interface Props {
  participant: IRecommendationParticipant;
}
const RecommendationSubmitted: React.FC<Props> = ({ participant }) => {
  const { currentUserID } = useCOCContextData();
  const { copy } = useCopyToClipboard();
  const { submissionContext } = useSubmissionContextData();
  const cocConfigRole = submissionContext?.config?.cocConfig?.find(
    user => !user.autoAssign && !user.isOwner
  )?.role;
  const recommendation = participant.recommendation;
  return (
    <>
      <Flex
        flexDirection="column"
        gap={4}
        backgroundColor="surfacePrimaryFill"
        mt={cocConfigRole !== participant.role ? 3 : 0}
      >
        <Flex justifyContent="space-between">
          <Flex flexDirection="column">
            <Flex flexDirection="row" gap={2}>
              <Text textStyle="headingLine">{participant?.userFirstName}</Text>
              {currentUserID === participant.userId && (
                <Badge variant="success">
                  <Flex flexDirection="row" alignItems="center" gap={1}>
                    <FontAwesomeIcon icon={faUser} fontSize={12} />
                    <Text>You</Text>
                  </Flex>
                </Badge>
              )}
            </Flex>
            <Text textStyle="bodyContext">{participant.role}</Text>
          </Flex>
          <Flex flexDirection="column">
            <Text textStyle="bodyContext">
              {participant?.completedAt
                ? formatDate(new Date(participant.completedAt))
                : '--'}
            </Text>
            <Box>
              {
                submissionStatusBadges[
                  participant.submissionWorkflowPhase || 'initial'
                ]
              }
            </Box>
          </Flex>
        </Flex>
        <Flex flexDirection="column">
          <Text textStyle="headingLine">{'Recommendation'}</Text>
          <Box>
            <RecommendationBadge
              recommendation={recommendation?.decision ?? participant.status}
            />
          </Box>
        </Flex>
        <Flex justifyContent="space-between">
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingLine">Comment for Author(s)</Text>
            <Text>{recommendation?.commentForAuthor || '--'}</Text>
          </Flex>
          <ButtonIcon
            ariaLabel="Copy"
            icon={<FontAwesomeIcon icon={faCopy} />}
            variant="primary"
            onClick={() => copy(recommendation?.commentForAuthor ?? '')}
            disabled={!recommendation?.commentForAuthor}
          />
        </Flex>

        <Flex flexDirection="column" gap={2}>
          <Text fontWeight="medium">
            {participant.role === cocConfigRole
              ? `Comment for Editor in
            Chief`
              : 'Comment for Staff'}
          </Text>
          <Text>{recommendation?.commentForEditor || '--'}</Text>
        </Flex>

        {recommendation?.attachments &&
        recommendation.attachments?.length > 0 ? (
          <Flex flexDirection="column" gap={2}>
            <Text fontWeight="medium">Attached File</Text>
            {recommendation?.attachments.map(
              (attachment: { name: string; url: string }, index: number) => (
                <Flex justifyContent="space-between" key={index}>
                  <Flex gap={2} alignItems="center" minWidth={0}>
                    <Text
                      textStyle="textBody"
                      whiteSpace="normal"
                      overflowWrap="anywhere"
                    >
                      {attachment.name}
                    </Text>
                    <FileViewerButtonText
                      file={attachment}
                      submissionContextId={submissionContext?.id}
                    >
                      View
                    </FileViewerButtonText>
                  </Flex>
                </Flex>
              )
            )}
          </Flex>
        ) : (
          <Text>No file attached</Text>
        )}
      </Flex>
    </>
  );
};

export default RecommendationSubmitted;
